import { ActionLink } from "@wine2trace/ui/FormComponents";
import { TbSparkles } from "react-icons/tb";
import { Icon } from "../Icon";
import { getCatastaFillEstimate } from "../../services/evaporationEstimates";
import { formatArrowQuantity, formatTankUnit } from "../../services/tankUnit";
import { PhaseCell } from "../LotList/lotCells";
import { getTankFill } from "./tankRows";

const formatNumber = (value, locale) => Number(value).toLocaleString(locale);

// "38.250 L" or, in the user's unit, "383 hL".
export const formatTankVolume = (liters, unit, locale) => {
  const { displayedQuantity, unit: shown } = formatTankUnit(liters, 0, unit);
  return `${formatNumber(displayedQuantity, locale)} ${shown}`;
};

export const formatTankCapacity = (tank, unit, locale) => {
  const { displayedCapacity, unit: shown } = formatTankUnit(
    0,
    tank?.capacity,
    unit,
  );
  return `${formatNumber(displayedCapacity, locale)} ${shown}`;
};

// "Serbatoio", "Barrique (4)": the type and, for grouped barrels, how many.
export const tankTypeLabel = (tank, getLabel) =>
  `${getLabel(`tankType${tank?.type}`)}${tank?.barrels_num > 1 ? ` (${tank.barrels_num})` : ""}`;

// Colour of the wine inside, as in the lot dot; none when the tank is empty.
export const wineColorClass = (tank) =>
  tank?.color ? `color_${String(tank.color).toLowerCase()}` : "";

// Meter of how full a tank is: the native <meter>, so there is no inline style and assistive technology
// reads it as a gauge. The percentage is always written next to it, the bar is only a help.
export const FillMeter = ({ tank, label }) => {
  const { percent, bar } = getTankFill(tank);
  return (
    <meter
      role="meter"
      className={`fill_meter ${wineColorClass(tank)}`.trim()}
      min={0}
      max={100}
      value={bar}
      aria-label={label}
      aria-valuetext={`${percent}%`}
    />
  );
};

// Fill of a tank: percentage and bar, then the volume and, for a catasta, the estimated evaporation.
export const TankFillCell = ({ tank, getLabel, unit, locale }) => {
  const { percent } = getTankFill(tank);
  const estimate = getCatastaFillEstimate(tank, tank?.winery_country);
  const evaporated = estimate?.estimatedEvaporationLiters || 0;
  return (
    <>
      <span className="data_cell_main tank_fill">
        <FillMeter
          tank={tank}
          label={getLabel("tanksFillLabel", { percent })}
        />
        <span className="tank_fill_percent">{`${percent}%`}</span>
      </span>
      <span className="data_cell_sub">
        {formatTankVolume(tank?.quantity, unit, locale)}
      </span>
      {evaporated > 0.5 && (
        <span
          className="data_cell_sub"
          title={getLabel("tankEstimatedEvaporationTooltip")}
        >
          {`${getLabel("tankEstimatedEvaporation")} ~${formatArrowQuantity(Math.round(evaporated), unit).value} ${unit}`}
        </span>
      )}
    </>
  );
};

// Lot inside the tank: wine-colour dot and link; below, its label. An empty tank says which lot is planned for it.
export const TankLotCell = ({ tank, getLabel }) => {
  if (tank?.batch_id) {
    return (
      <>
        <span className="lot_name">
          {tank.color && (
            <span
              className={`lot_color_dot ${wineColorClass(tank)}`}
              role="img"
              aria-label={getLabel("lotsColorOf", {
                color: getLabel(`lotColor_${tank.color}`),
              })}
            />
          )}
          <ActionLink to={`/lots/${tank.batch_id}`} variant="strong">
            {tank.batch_name}
          </ActionLink>
        </span>
        {tank.batch_label && (
          <span className="data_cell_sub">{tank.batch_label}</span>
        )}
      </>
    );
  }
  if (tank?.planned_batch_name) {
    return (
      <span className="data_cell_sub">
        {getLabel("tanksPlannedLot", { name: tank.planned_batch_name })}
      </span>
    );
  }
  return "—";
};

// "Sanitized" badge of an empty tank: a soft green pill with an icon, so it stands out without shouting.
export const TankSanitized = ({ getLabel }) => (
  <span className="tank_sanitized">
    <Icon icon={TbSparkles} size="sm" aria-hidden="true" />
    {getLabel("tankSanitized_OK")}
  </span>
);

// "Vino", "Affinamento"...: the phase of the lot in the tank.
export const tankPhaseLabel = (phase, getLabel) =>
  getLabel(phase === "AGING" ? "lotsPhase_AGING" : `lotState_${phase}`);

// Phase and state of what is in the tank, as for a lot; an empty tank says so (and whether it is sanitized).
export const TankPhaseCell = ({
  tank,
  getLabel,
  running,
  treatments,
  analysis,
}) => {
  const isEmpty = !tank?.state;
  return (
    <>
      <PhaseCell
        lot={{
          state: tank?.state,
          substate: tank?.tank_substate?.substate,
          batch_type: tank?.batch_type,
          status: "CONFIRMED",
        }}
        getLabel={getLabel}
        onlyConfirmed
        running={running}
        treatments={treatments}
        analysis={analysis}
      />
      {isEmpty &&
        (tank?.is_sanitized ? (
          <TankSanitized getLabel={getLabel} />
        ) : (
          <span className="data_cell_sub">
            {getLabel("tankSanitized_NOT_OK")}
          </span>
        ))}
    </>
  );
};
